import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./MonthlyExpensesEditor.css";
import { 
  useMonthlyExpenses,
  useAddMonthlyExpense,
  useUpdateMonthlyExpense,
  useDeleteMonthlyExpense,
  useAddMonthlyIncome,
  useUpdateMonthlyIncome,
  useDeleteMonthlyIncome,
  useAddMonthlyBudget,
  useUpdateMonthlyBudget,
  useDeleteMonthlyBudget
} from '../hooks/useApi';
import ConfirmationDialog from './ConfirmationDialog';
import { useToast } from './Toast';

const MonthlyExpensesEditor = ({ onSaveSuccess }) => {
  const [newExpense, setNewExpense] = useState({ name: "", amount: "", category: "" });
  const [newIncome, setNewIncome] = useState({ name: "", amount: "" });
  const [newBudget, setNewBudget] = useState({ category: "", amount: "" });
  const [addType, setAddType] = useState("expense");
  const [editingId, setEditingId] = useState(null);
  const [editingType, setEditingType] = useState(null); // 'expense', 'income', or 'budget'
  const [isMobile, setIsMobile] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, type: null });
  const { showToast } = useToast();

  // Mobile detection hook
  useEffect(() => {
    const checkScreenSize = () => {
      // Use a higher breakpoint for better mobile detection
      const screenWidth = window.innerWidth;
      const isMobileScreen = screenWidth < 900; // Increased from 768
      
      // Also check for touch support (Chrome DevTools simulation includes this)
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Check for mobile user agent (Chrome DevTools changes this)
      const isMobileUA = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      const isMobileDevice = isMobileScreen || hasTouch || isMobileUA;
      
      console.log('Screen width:', screenWidth, 'Touch support:', hasTouch, 'Mobile UA:', isMobileUA, 'Final isMobile:', isMobileDevice);
      setIsMobile(isMobileDevice);
    };
    
    // Initial check
    checkScreenSize();
    
    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);
    
    // Also listen for orientation change (for mobile devices)
    window.addEventListener('orientationchange', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
      window.removeEventListener('orientationchange', checkScreenSize);
    };
  }, []);

  // React Query hooks
  const { data: monthlyData = { expenses: [], income: [], budget: [] }, isLoading, error } = useMonthlyExpenses();
  
  // Mutation hooks
  const addExpenseMutation = useAddMonthlyExpense();
  const updateExpenseMutation = useUpdateMonthlyExpense();
  const deleteExpenseMutation = useDeleteMonthlyExpense();
  const addIncomeMutation = useAddMonthlyIncome();
  const updateIncomeMutation = useUpdateMonthlyIncome();
  const deleteIncomeMutation = useDeleteMonthlyIncome();
  const addBudgetMutation = useAddMonthlyBudget();
  const updateBudgetMutation = useUpdateMonthlyBudget();
  const deleteBudgetMutation = useDeleteMonthlyBudget();

  const handleAddExpense = async () => {
    if (newExpense.name && newExpense.amount && newExpense.category) {
      try {
        await addExpenseMutation.mutateAsync(newExpense);
        setNewExpense({ name: "", amount: "", category: "" });
        if (onSaveSuccess) onSaveSuccess();
        showToast('Expense added successfully!', 'success');
      } catch (err) {
        showToast("Error adding expense: " + err.message, 'error');
      }
    }
  };

  const handleAddIncome = async () => {
    if (newIncome.name && newIncome.amount) {
      try {
        await addIncomeMutation.mutateAsync({
          ...newIncome,
          category: '' // API expects category parameter even for income
        });
        setNewIncome({ name: "", amount: "" });
        if (onSaveSuccess) onSaveSuccess();
        showToast('Income added successfully!', 'success');
      } catch (err) {
        showToast("Error adding income: " + err.message, 'error');
      }
    }
  };

  const handleAddBudget = async () => {
    if (newBudget.category && newBudget.amount) {
      try {
        await addBudgetMutation.mutateAsync(newBudget);
        setNewBudget({ category: "", amount: "" });
        if (onSaveSuccess) onSaveSuccess();
        showToast('Budget item added successfully!', 'success');
      } catch (err) {
        showToast("Error adding budget item: " + err.message, 'error');
      }
    }
  };

  const handleDelete = (id, type) => {
    setDeleteConfirm({ isOpen: true, id, type });
  };

  const confirmDelete = async () => {
    const { id, type } = deleteConfirm;
    setDeleteConfirm({ isOpen: false, id: null, type: null });
    try {
      switch (type) {
        case 'expense':
          await deleteExpenseMutation.mutateAsync(id);
          break;
        case 'income':
          await deleteIncomeMutation.mutateAsync(id);
          break;
        case 'budget':
          await deleteBudgetMutation.mutateAsync(id);
          break;
      }
      if (onSaveSuccess) onSaveSuccess();
      showToast(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully!`, 'success');
    } catch (err) {
      showToast(`Error deleting ${type}: ` + err.message, 'error');
    }
  };

  const handleEdit = (id, type) => {
    setEditingId(id);
    setEditingType(type);
  };

  const handleSaveEdit = async (id, type, data) => {
    try {
      switch (type) {
        case 'expense':
          await updateExpenseMutation.mutateAsync({
            id,
            name: data.name,
            amount: parseFloat(data.amount),
            category: data.category
          });
          break;
        case 'income':
          await updateIncomeMutation.mutateAsync({
            id,
            name: data.name,
            amount: parseFloat(data.amount),
            category: '' // API expects category even for income updates
          });
          break;
        case 'budget':
          await updateBudgetMutation.mutateAsync({
            id,
            category: data.category,
            amount: parseFloat(data.amount)
          });
          break;
      }
      setEditingId(null);
      setEditingType(null);
      if (onSaveSuccess) onSaveSuccess();
      showToast(`${type.charAt(0).toUpperCase() + type.slice(1)} updated successfully!`, 'success');
    } catch (err) {
      showToast(`Error updating ${type}: ` + err.message, 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingType(null);
  };

  if (isLoading) return <div>Loading monthly data...</div>;
  if (error) return <div style={{ color: "red" }}>Error: {error.message}</div>;

  const isAnyMutationLoading = 
    addExpenseMutation.isPending || updateExpenseMutation.isPending || deleteExpenseMutation.isPending ||
    addIncomeMutation.isPending || updateIncomeMutation.isPending || deleteIncomeMutation.isPending ||
    addBudgetMutation.isPending || updateBudgetMutation.isPending || deleteBudgetMutation.isPending;

  return (
    <div className="monthly-expenses-editor">
      <h2>Monthly Financial Data Editor</h2>

      {/* Add Type Selector */}
      <div style={{ marginBottom: "20px" }}>
        <label style={{ 
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500'
        }}>Add: </label>
        <select 
          value={addType} 
          onChange={(e) => setAddType(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '300px',
            padding: '8px',
            fontSize: '14px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: 'white'
          }}
        >
          <option value="expense">Expense</option>
          <option value="income">Income</option>
          <option value="budget">Budget Item</option>
        </select>
      </div>

      {/* Add New Item Form */}
      {addType === "expense" && (
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Add New Expense</h4>
          <div style={{
            display: 'grid',
            gap: '12px',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
            marginBottom: '16px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>Name</label>
              <input
                type="text"
                placeholder="Expense name"
                value={newExpense.name}
                onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                disabled={addExpenseMutation.isPending}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>Amount</label>
              <input
                type="number"
                step="0.01"
                placeholder="Amount"
                value={newExpense.amount}
                onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                disabled={addExpenseMutation.isPending}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>Category</label>
              <input
                type="text"
                placeholder="Category"
                value={newExpense.category}
                onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                disabled={addExpenseMutation.isPending}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
          <button 
            onClick={handleAddExpense}
            disabled={addExpenseMutation.isPending || !newExpense.name || !newExpense.amount || !newExpense.category}
            style={{
              width: isMobile ? '100%' : 'auto',
              backgroundColor: '#10b981',
              color: 'white',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              borderRadius: '4px',
              cursor: addExpenseMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: (addExpenseMutation.isPending || !newExpense.name || !newExpense.amount || !newExpense.category) ? 0.6 : 1
            }}
          >
            {addExpenseMutation.isPending ? "Adding..." : "Add Expense"}
          </button>
        </div>
      )}

      {addType === "income" && (
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Add New Income</h4>
          <div style={{
            display: 'grid',
            gap: '12px',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            marginBottom: '16px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>Source</label>
              <input
                type="text"
                placeholder="Income source"
                value={newIncome.name}
                onChange={(e) => setNewIncome({ ...newIncome, name: e.target.value })}
                disabled={addIncomeMutation.isPending}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>Amount</label>
              <input
                type="number"
                step="0.01"
                placeholder="Amount"
                value={newIncome.amount}
                onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                disabled={addIncomeMutation.isPending}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
          <button 
            onClick={handleAddIncome}
            disabled={addIncomeMutation.isPending || !newIncome.name || !newIncome.amount}
            style={{
              width: isMobile ? '100%' : 'auto',
              backgroundColor: '#10b981',
              color: 'white',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              borderRadius: '4px',
              cursor: addIncomeMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: (addIncomeMutation.isPending || !newIncome.name || !newIncome.amount) ? 0.6 : 1
            }}
          >
            {addIncomeMutation.isPending ? "Adding..." : "Add Income"}
          </button>
        </div>
      )}

      {addType === "budget" && (
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Add New Budget Item</h4>
          <div style={{
            display: 'grid',
            gap: '12px',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            marginBottom: '16px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>Category</label>
              <input
                type="text"
                placeholder="Budget category"
                value={newBudget.category}
                onChange={(e) => setNewBudget({ ...newBudget, category: e.target.value })}
                disabled={addBudgetMutation.isPending}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>Amount</label>
              <input
                type="number"
                step="0.01"
                placeholder="Budget amount"
                value={newBudget.amount}
                onChange={(e) => setNewBudget({ ...newBudget, amount: e.target.value })}
                disabled={addBudgetMutation.isPending}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
          <button 
            onClick={handleAddBudget}
            disabled={addBudgetMutation.isPending || !newBudget.category || !newBudget.amount}
            style={{
              width: isMobile ? '100%' : 'auto',
              backgroundColor: '#10b981',
              color: 'white',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              borderRadius: '4px',
              cursor: addBudgetMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: (addBudgetMutation.isPending || !newBudget.category || !newBudget.amount) ? 0.6 : 1
            }}
          >
            {addBudgetMutation.isPending ? "Adding..." : "Add Budget Item"}
          </button>
        </div>
      )}

      {/* Expenses Section */}
      <div style={{ marginTop: "30px" }}>
        <h3>Monthly Expenses ({monthlyData.expenses?.length || 0} items) {isMobile ? '(Mobile View)' : '(Desktop View)'}</h3>
        <p>Screen width: {typeof window !== 'undefined' ? window.innerWidth : 'unknown'}px | Mobile: {isMobile ? 'YES' : 'NO'}</p>
        
        {/* Mobile Card View */}
        {isMobile && (
          <div>
            {monthlyData.expenses?.map((item) => (
              <div key={item.id} style={{ 
                backgroundColor: '#f9fafb', 
                padding: '16px', 
                marginBottom: '12px', 
                borderRadius: '8px', 
                border: '1px solid #d1d5db' 
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#374151', 
                    marginBottom: '4px' 
                  }}>Name</label>
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{item.name}</span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#374151', 
                    marginBottom: '4px' 
                  }}>Amount</label>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#dc2626' }}>${item.amount}</span>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#374151', 
                    marginBottom: '4px' 
                  }}>Category</label>
                  <span style={{ fontSize: '16px' }}>{item.category}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    style={{ 
                      flex: 1,
                      backgroundColor: '#3b82f6', 
                      color: 'white', 
                      padding: '8px 16px', 
                      fontSize: '14px', 
                      borderRadius: '4px', 
                      border: 'none', 
                      cursor: 'pointer' 
                    }}
                    onClick={() => handleEdit(item.id, 'expense')}
                    disabled={isAnyMutationLoading}
                  >
                    Edit
                  </button>
                  <button 
                    style={{ 
                      flex: 1,
                      backgroundColor: '#ef4444', 
                      color: 'white', 
                      padding: '8px 16px', 
                      fontSize: '14px', 
                      borderRadius: '4px', 
                      border: 'none', 
                      cursor: 'pointer' 
                    }}
                    onClick={() => handleDelete(item.id, 'expense')}
                    disabled={isAnyMutationLoading}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Desktop Table View */}
        {!isMobile && (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Amount</th>
                <th>Category</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.expenses?.map((item) => (
                <ExpenseRow
                  key={item.id}
                  item={item}
                  type="expense"
                  isEditing={editingId === item.id && editingType === 'expense'}
                  onEdit={handleEdit}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                  onDelete={handleDelete}
                  isLoading={isAnyMutationLoading}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Income Section */}
      <div style={{ marginTop: "30px" }}>
        <h3>Monthly Income ({monthlyData.income?.length || 0} items)</h3>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {monthlyData.income?.map((item) => (
              <IncomeRow
                key={item.id}
                item={item}
                type="income"
                isEditing={editingId === item.id && editingType === 'income'}
                onEdit={handleEdit}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onDelete={handleDelete}
                isLoading={isAnyMutationLoading}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Budget Section */}
      <div style={{ marginTop: "30px" }}>
        <h3>Monthly Budget ({monthlyData.budget?.length || 0} items)</h3>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {monthlyData.budget?.map((item) => (
              <BudgetRow
                key={item.id}
                item={item}
                type="budget"
                isEditing={editingId === item.id && editingType === 'budget'}
                onEdit={handleEdit}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onDelete={handleDelete}
                isLoading={isAnyMutationLoading}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null, type: null })}
        onConfirm={confirmDelete}
        title={`Delete ${deleteConfirm.type ? deleteConfirm.type.charAt(0).toUpperCase() + deleteConfirm.type.slice(1) : 'Item'}`}
        message={`Are you sure you want to delete this ${deleteConfirm.type || 'item'}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Row components for each type
const ExpenseRow = ({ item, type, isEditing, onEdit, onSaveEdit, onCancelEdit, onDelete, isLoading }) => {
  const [editValues, setEditValues] = useState({
    name: item.name,
    amount: item.amount,
    category: item.category
  });

  const handleSave = () => {
    onSaveEdit(item.id, type, editValues);
  };

  const handleCancel = () => {
    setEditValues({ name: item.name, amount: item.amount, category: item.category });
    onCancelEdit();
  };

  if (isEditing) {
    return (
      <tr>
        <td>
          <input
            type="text"
            value={editValues.name}
            onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
            disabled={isLoading}
          />
        </td>
        <td>
          <input
            type="number"
            step="0.01"
            value={editValues.amount}
            onChange={(e) => setEditValues({ ...editValues, amount: e.target.value })}
            disabled={isLoading}
          />
        </td>
        <td>
          <input
            type="text"
            value={editValues.category}
            onChange={(e) => setEditValues({ ...editValues, category: e.target.value })}
            disabled={isLoading}
          />
        </td>
        <td>
          <button onClick={handleSave} disabled={isLoading} style={{ marginRight: "5px" }}>
            {isLoading ? "Saving..." : "Save"}
          </button>
          <button onClick={handleCancel} disabled={isLoading}>Cancel</button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{item.name}</td>
      <td>${item.amount}</td>
      <td>{item.category}</td>
      <td>
        <button onClick={() => onEdit(item.id, type)} disabled={isLoading} style={{ marginRight: "5px" }}>
          Edit
        </button>
        <button 
          onClick={() => onDelete(item.id, type)} 
          disabled={isLoading}
          style={{ 
            backgroundColor: "#dc3545",
            color: "white",
            border: "1px solid #dc3545",
            padding: "4px 8px",
            borderRadius: "4px",
            cursor: isLoading ? "not-allowed" : "pointer",
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {isLoading ? "Deleting..." : "Delete"}
        </button>
      </td>
    </tr>
  );
};

const IncomeRow = ({ item, type, isEditing, onEdit, onSaveEdit, onCancelEdit, onDelete, isLoading }) => {
  const [editValues, setEditValues] = useState({
    name: item.name,
    amount: item.amount
  });

  const handleSave = () => {
    onSaveEdit(item.id, type, editValues);
  };

  const handleCancel = () => {
    setEditValues({ name: item.name, amount: item.amount });
    onCancelEdit();
  };

  if (isEditing) {
    return (
      <tr>
        <td>
          <input
            type="text"
            value={editValues.name}
            onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
            disabled={isLoading}
          />
        </td>
        <td>
          <input
            type="number"
            step="0.01"
            value={editValues.amount}
            onChange={(e) => setEditValues({ ...editValues, amount: e.target.value })}
            disabled={isLoading}
          />
        </td>
        <td>
          <button onClick={handleSave} disabled={isLoading} style={{ marginRight: "5px" }}>
            {isLoading ? "Saving..." : "Save"}
          </button>
          <button onClick={handleCancel} disabled={isLoading}>Cancel</button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{item.name}</td>
      <td>${item.amount}</td>
      <td>
        <button onClick={() => onEdit(item.id, type)} disabled={isLoading} style={{ marginRight: "5px" }}>
          Edit
        </button>
        <button 
          onClick={() => onDelete(item.id, type)} 
          disabled={isLoading}
          style={{ 
            backgroundColor: "#dc3545",
            color: "white",
            border: "1px solid #dc3545",
            padding: "4px 8px",
            borderRadius: "4px",
            cursor: isLoading ? "not-allowed" : "pointer",
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {isLoading ? "Deleting..." : "Delete"}
        </button>
      </td>
    </tr>
  );
};

const BudgetRow = ({ item, type, isEditing, onEdit, onSaveEdit, onCancelEdit, onDelete, isLoading }) => {
  const [editValues, setEditValues] = useState({
    category: item.category,
    amount: item.amount
  });

  const handleSave = () => {
    onSaveEdit(item.id, type, editValues);
  };

  const handleCancel = () => {
    setEditValues({ category: item.category, amount: item.amount });
    onCancelEdit();
  };

  if (isEditing) {
    return (
      <tr>
        <td>
          <input
            type="text"
            value={editValues.category}
            onChange={(e) => setEditValues({ ...editValues, category: e.target.value })}
            disabled={isLoading}
          />
        </td>
        <td>
          <input
            type="number"
            step="0.01"
            value={editValues.amount}
            onChange={(e) => setEditValues({ ...editValues, amount: e.target.value })}
            disabled={isLoading}
          />
        </td>
        <td>
          <button onClick={handleSave} disabled={isLoading} style={{ marginRight: "5px" }}>
            {isLoading ? "Saving..." : "Save"}
          </button>
          <button onClick={handleCancel} disabled={isLoading}>Cancel</button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{item.category}</td>
      <td>${item.amount}</td>
      <td>
        <button onClick={() => onEdit(item.id, type)} disabled={isLoading} style={{ marginRight: "5px" }}>
          Edit
        </button>
        <button 
          onClick={() => onDelete(item.id, type)} 
          disabled={isLoading}
          style={{ 
            backgroundColor: "#dc3545",
            color: "white",
            border: "1px solid #dc3545",
            padding: "4px 8px",
            borderRadius: "4px",
            cursor: isLoading ? "not-allowed" : "pointer",
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {isLoading ? "Deleting..." : "Delete"}
        </button>
      </td>
    </tr>
  );
};

MonthlyExpensesEditor.propTypes = {
  onSaveSuccess: PropTypes.func,
};

ExpenseRow.propTypes = {
  item: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

IncomeRow.propTypes = {
  item: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

BudgetRow.propTypes = {
  item: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default MonthlyExpensesEditor;
